// Utilitários do modelo "Atelier" (topo 12 · busca 15 · card 9 · rodapé 9) — revista de
// arquitetura: papel, tinta, linhas finas e UM acento (a cor do tenant). Importado pelas 4 peças
// do Atelier e, nas internas (listagem/ficha), só atrás de `paginaInternaAtelier` (topo 12).
// Os helpers genéricos de tenant (contatos, menu, redes, WhatsApp, movimento reduzido) são os
// MESMOS do Horizonte, re-exportados para as peças do Atelier importarem de um lugar só.
import type { CSSProperties } from 'react';
import { ensureReadableTextColor, gera_titulo, getContrastRatio, isColorLight, marca_dagua, NumeroFormatado, removerAcentos, toPrice } from '../../../utils/FunctionsGlobals';
import { getSafeImageSrc, preferGrandePhoto, PROPERTY_IMAGE_FALLBACK } from '../../../utils/image';
import { appendCodsiteQuery } from '../../../utils/url';
import { hifenUrl } from '../../../utils/hifenUrl';
import { hexParaRgba, misturarCores } from '../horizonte/horizonte';
import type { FormatadorPreco } from '../../../utils/moedaSite';

export { contatosDoTenant, hexParaRgba, linkWhatsApp, linkWhatsAppDoTenant, menuDoTenant, mesmaRota, misturarCores, redesDoTenant, useMovimentoReduzido } from '../horizonte/horizonte';
export type { ItemMenu, TelefoneTenant } from '../horizonte/horizonte';

// Curva editorial de todas as entradas do modelo (desacelera longo no fim).
export const EASE_ATELIER = [0.22, 1, 0.36, 1] as const;

// Identidade fixa do modelo (a paleta do tenant entra só como ACENTO).
export const PAPEL = '#f5f1ea';
export const TINTA = '#161514';
export const CINZA = '#6f6a63';
export const LINHA = '#ddd6cb';

export interface PaletaAtelier {
    papel: string;
    tinta: string;
    cinza: string;
    linha: string;
    /** cor principal do tenant, puxada para a tinta só até ler 3:1 sobre o papel. */
    acento: string;
    /** texto sobre o acento (botão principal) — calculado, nunca chutado. */
    acentoTexto: string;
}

/** Acento = cor2 (ou cor1) do tenant; cor clara demais sobre o papel é escurecida até ler 3:1. */
export const paletaAtelier = (site: any): PaletaAtelier => {
    const marca = site?.cor2 || site?.cor1 || '#8a6d3b';
    let acento = TINTA;
    for (let t = 0; t <= 1.0001; t += 0.1) {
        const c = t === 0 ? marca : misturarCores(marca, TINTA, t);
        if (getContrastRatio(c, PAPEL) >= 3) {
            acento = c;
            break;
        }
    }
    const acentoTexto = ensureReadableTextColor(isColorLight(acento) ? site?.cor11 : site?.cor13, acento) || (isColorLight(acento) ? TINTA : '#ffffff');
    return { papel: PAPEL, tinta: TINTA, cinza: CINZA, linha: LINHA, acento, acentoTexto };
};

/** Variáveis CSS (--at-*) que os 4 módulos consomem; o Builder pode sobrescrevê-las por CSS custom. */
export const varsAtelier = (site: any): CSSProperties => {
    const p = paletaAtelier(site);
    return {
        '--at-papel': p.papel,
        '--at-tinta': p.tinta,
        '--at-cinza': p.cinza,
        '--at-linha': p.linha,
        '--at-acento': p.acento,
        '--at-acento-texto': p.acentoTexto,
        '--at-acento-suave': hexParaRgba(p.acento, 0.12),
        '--at-papel-vidro': hexParaRgba(p.papel, 0.86, 'rgba(245, 241, 234, ALPHA)'),
    } as CSSProperties;
};

// ---------------------------------------------------------------------------
// Páginas internas (listagem /imoveis e ficha do imóvel) com o topo do Atelier.
// ---------------------------------------------------------------------------
/** O topo 12 é o que o visitante vê em TODAS as páginas: com ele, as internas vestem o Atelier. */
export const paginaInternaAtelier = (site: any): boolean => Number(site?.modelo_topo) === 12;

const sitesComPaleta = new WeakMap<object, any>();
/**
 * O site com as cores do CRM trocadas pela paleta do Atelier (papel, tinta e o acento do tenant),
 * para as peças das internas que pintam direto de cor2/cor5/cor11/cor12/cor13/cor15 seguirem a
 * home. Fora do Atelier devolve o MESMO objeto (diff nulo); no Atelier, sempre a mesma cópia por
 * objeto de entrada (WeakMap), para não quebrar memo/efeitos que dependem de `site`.
 */
export const siteComPaletaAtelier = (site: any): any => {
    if (!site || typeof site !== 'object' || !paginaInternaAtelier(site)) return site;
    const pronto = sitesComPaleta.get(site);
    if (pronto) return pronto;
    const p = paletaAtelier(site);
    const copia = {
        ...site,
        cor2: p.acento,
        cor5: PAPEL,
        cor11: TINTA,
        cor12: '#ffffff',
        cor13: PAPEL,
        cor15: '#ece6db',
    };
    sitesComPaleta.set(site, copia);
    return copia;
};

// ---------------------------------------------------------------------------
// Imóvel (slide dos carrosséis / novidades): mesmas regras do CardHorizonte/CardAurora.
// ---------------------------------------------------------------------------

/** URL da ficha do imóvel (idêntica à dos cards existentes, com o codsite de teste quando houver). */
export const urlDoImovel = (slide: any, site: any, codsite?: any): string => {
    const validationModel = slide?.venda ? 'venda' : slide?.aluguel ? 'aluguel' : 'venda';
    const hideCompanyInUrl = Number(site?.id) === 2551;
    const newPath = `/imoveis/${validationModel}/${
        slide?.cidade?.nome ? hifenUrl(slide?.cidade?.nome, site?.id).replaceAll('/', '-').replaceAll('?', '-') : '-'
    }/${slide?.bairro?.nome ? slide?.bairro?.nome.replaceAll('/', '-').replaceAll('?', '-') : '-'}/${
        site?.exibir_empreendimento && !hideCompanyInUrl ? slide?.empreendimento?.nome?.replaceAll('\n', '-').replaceAll('.', '-') || '-' : '-'
    }/${slide?.tipo?.nome ? hifenUrl(slide?.tipo?.nome, site?.id).replaceAll('/', '_') : '-'}/${
        slide?.referencia ? hifenUrl(slide?.referencia, site?.id).replaceAll('/', '_') : '-'
    }/imovel/${slide?.id}`.replaceAll(' ', '-');
    return appendCodsiteQuery(removerAcentos(newPath).toLowerCase(), codsite);
};

/** Fotos brutas do imóvel (array OU objeto único) + foto do empreendimento, sem repetição. */
export const fotosDoImovel = (slide: any): string[] => {
    const brutas: any[] = Array.isArray(slide?.fotos) ? slide.fotos : slide?.fotos?.url_foto ? [slide.fotos] : [];
    const urls = brutas.map((f) => (typeof f === 'string' ? f : f?.url_foto)).filter((u): u is string => typeof u === 'string' && u.length > 0);
    if (slide?.empreendimento?.foto_destaque) urls.push(String(slide.empreendimento.foto_destaque));
    return Array.from(new Set(urls));
};

/** Foto otimizada (marca d'água do tenant quando ativada; tamanho por dispositivo) com fallback seguro. */
export const fotoOtimizada = (url: string | undefined, site: any, celular: boolean): string => {
    const base = url || PROPERTY_IMAGE_FALLBACK;
    const bruta = site?.marca_dagua?.ativada ? marca_dagua(base, site, celular ? 'm' : 'g') : preferGrandePhoto(base, !celular);
    return getSafeImageSrc(bruta, PROPERTY_IMAGE_FALLBACK);
};

/** Preço principal/secundário com "Sob consulta" e moeda — regra dos cards existentes. */
export const precoDoImovel = (slide: any, site: any, fmt?: FormatadorPreco): { principal: string; secundario: string } => {
    // 21/10: `fmt` (usePrecoMoeda) converte para a moeda do idioma; sem ele, o texto de sempre.
    const moeda = (preco: number) => {
        const simbolo = slide?.moeda_imovel && slide?.moeda_imovel != site?.moeda_tipo ? slide?.moeda_imovel : site?.moeda_tipo;
        const original = `${simbolo} ${toPrice(preco, 2)}`;
        return fmt ? fmt(preco, simbolo, original) : original;
    };
    const showVenda = Number(slide?.exibir_preco_venda && slide?.preco_venda > 0);
    const showLocacao = Number(slide?.aluguel > 0 && slide?.exibir_preco_locacao && slide?.preco_locacao > 0);
    const principal = showVenda
        ? moeda(slide?.preco_venda)
        : slide?.venda == 1
        ? 'Sob consulta'
        : showLocacao
        ? `${moeda(slide?.preco_locacao)}${slide?.aluguel ? '/mês' : ''}`
        : slide?.aluguel == 1
        ? 'Sob consulta'
        : '';
    const secundario = showVenda && showLocacao ? `Locação ${moeda(slide?.preco_locacao)}${slide?.aluguel ? '/mês' : ''}` : '';
    return { principal, secundario };
};

export const finalidadeDoImovel = (slide: any): string => (slide?.venda ? 'Venda' : slide?.aluguel ? 'Locação' : slide?.temporada ? 'Temporada' : '');

/** Título: o cadastrado ou o gerado (área privativa/total sempre em m² — #1070). */
export const tituloDoImovel = (slide: any, site: any): string =>
    slide?.titulo ? slide.titulo : gera_titulo(slide?.area_privativa > 0 || slide?.area_total > 0 ? { ...slide, area_tipo: 'm²' } : slide, site);

export type ItemFicha = { icone: 'area' | 'quartos' | 'suites' | 'vagas'; valor: string; rotulo: string };

/** Ficha curta: área preferida (privativa > total > terreno) · quartos · suítes · vagas. */
export const fichaDoImovel = (slide: any): ItemFicha[] => {
    const temArea = slide?.area_privativa > 0 || slide?.area_total > 0 || slide?.terreno > 0;
    const area = temArea
        ? `${NumeroFormatado(Number(slide?.area_privativa > 0 ? slide?.area_privativa : slide?.area_total > 0 ? slide?.area_total : slide?.terreno))} ${
              slide?.area_privativa > 0 || slide?.area_total > 0 ? 'm²' : slide?.area_tipo || 'm²'
          }`
        : '';
    const n = (v: any) => Number(v) || 0;
    return [
        area ? { icone: 'area' as const, valor: area, rotulo: '' } : null,
        n(slide?.dormitorios) > 0 ? { icone: 'quartos' as const, valor: String(n(slide.dormitorios)), rotulo: n(slide.dormitorios) === 1 ? 'quarto' : 'quartos' } : null,
        n(slide?.suites) > 0 ? { icone: 'suites' as const, valor: String(n(slide.suites)), rotulo: n(slide.suites) === 1 ? 'suíte' : 'suítes' } : null,
        n(slide?.garagems) > 0 ? { icone: 'vagas' as const, valor: String(n(slide.garagems)), rotulo: n(slide.garagems) === 1 ? 'vaga' : 'vagas' } : null,
    ].filter(Boolean) as ItemFicha[];
};

// ---------------------------------------------------------------------------
// Busca em frase ("Quero comprar um apartamento em Centro até R$ 500 mil").
// ---------------------------------------------------------------------------

const TIPOS_FEMININOS = new Set(['garagem', 'kitnet', 'kitinete', 'quitinete', 'mansao', 'suite', 'vaga', 'loja', 'area', 'sala', 'casa', 'chacara', 'fazenda', 'cobertura', 'pousada']);

/** "um apartamento" / "uma casa" / "um imóvel" — concordância pelo 1º termo do tipo. */
export const tipoComArtigo = (nome: string | undefined | null): string => {
    const limpo = String(nome ?? '').trim();
    if (!limpo) return 'um imóvel';
    const primeiro = removerAcentos(limpo.split(/\s+/)[0]).toLowerCase();
    const feminino = TIPOS_FEMININOS.has(primeiro) || (primeiro.endsWith('a') && !primeiro.endsWith('ma'));
    return `${feminino ? 'uma' : 'um'} ${limpo.toLowerCase()}`;
};

/** Verbo da transação no lugar do rótulo: venda → comprar, aluguel → alugar, temporada → alugar por temporada. */
export const verboDaTransacao = (value: string | undefined | null, label?: string): string => {
    const v = String(value ?? '').toLowerCase();
    if (v === 'venda') return 'comprar';
    if (v === 'aluguel') return 'alugar';
    if (v === 'temporada') return 'alugar por temporada';
    return String(label || v || 'comprar').toLowerCase();
};

export interface OpcaoValor {
    label: string;
    value: string;
}

const ESCADA_VENDA = [100000, 150000, 200000, 300000, 400000, 500000, 600000, 800000, 1000000, 1500000, 2000000, 3000000, 5000000, 8000000, 10000000, 15000000, 20000000, 30000000, 50000000];
const ESCADA_LOCACAO = [500, 800, 1000, 1500, 2000, 2500, 3000, 4000, 5000, 7000, 10000, 15000, 20000, 30000, 50000];

/** "R$ 500 mil" / "R$ 1,5 mi" / "R$ 2.500" — determinístico (sem Intl), igual no SSR e no cliente. */
export const valorCompacto = (valor: number, moeda: string): string => {
    const fmt = (n: number) => String(Math.round(n * 10) / 10).replace('.', ',');
    if (valor >= 1000000) return `${moeda} ${fmt(valor / 1000000)} mi`;
    if (valor >= 1000 && valor % 1000 === 0) return `${moeda} ${valor / 1000} mil`;
    return `${moeda} ${toPrice(valor, 0)}`;
};

/**
 * Degraus de "até R$ X" dentro da faixa REAL do tenant (min/max da transação — os mesmos
 * `valores` que os heros existentes recebem). Sem faixa conhecida: só "qualquer valor".
 */
export const escadaDeValores = (faixa: { min?: any; max?: any } | null | undefined, transacao: string, moeda: string): OpcaoValor[] => {
    const base: OpcaoValor[] = [{ label: 'qualquer valor', value: '' }];
    const max = Number(faixa?.max);
    const min = Number(faixa?.min) || 0;
    if (!Number.isFinite(max) || max <= 0) return base;
    const escada = transacao === 'venda' ? ESCADA_VENDA : ESCADA_LOCACAO;
    const dentro = escada.filter((v) => v > min && v < max);
    // no máximo 7 degraus, espalhados pela faixa
    const passo = Math.max(1, Math.ceil(dentro.length / 7));
    const escolhidos = dentro.filter((_, i) => i % passo === 0).slice(0, 7);
    return base.concat(escolhidos.map((v) => ({ label: `até ${valorCompacto(v, moeda)}`, value: String(v) })));
};

/** URL da listagem por cidade e/ou tipo — mesmo formato dos rodapés existentes (segmentos com hífen escapado). */
export const urlDaListagem = (transacao: string, cidade?: string, tipo?: string, codsite?: unknown): string => {
    const seg = (v?: string) => {
        const raw = String(v ?? '').trim();
        return raw ? hifenUrl(raw, codsite) : '-';
    };
    return removerAcentos(`/imoveis/${transacao}/${seg(cidade)}/-/-/${seg(tipo)}`)
        .toLowerCase()
        .replaceAll(' ', '-');
};

// ---------------------------------------------------------------------------
// Logo por fundo. O cadastro tem UMA logo (feita para o cabeçalho); o Atelier põe a marca em três
// fundos: tinta (topo), cor1 (selo da tela de filial) e papel (rodapé). Tenant que mandou uma arte
// para cada fundo entra no mapa (gate por codsite); o resto segue com a url_logo do cadastro.
// ⚠️ As do mapa são FIXAS: trocar a logo no CRM não muda o Atelier desse tenant.
// ---------------------------------------------------------------------------
export type FundoDaLogo = 'preto' | 'azul' | 'branco';

interface LogosPorFundo extends Partial<Record<FundoDaLogo, string>> {
    /** arte em pé (símbolo em cima do nome): ganha mais altura, senão o nome fica miúdo */
    empilhada?: boolean;
}

const CDN_LOGOS = 'https://fotos2.fra1.cdn.digitaloceanspaces.com/sites';
const LOGOS_POR_FUNDO: Record<number, LogosPorFundo> = {
    // cs834 Cecília (21/10): PNG transparente recortado das 3 JPEGs do cliente (834_preto/azul/branco)
    834: {
        preto: `${CDN_LOGOS}/834/logo-fundo-preto.png`,
        azul: `${CDN_LOGOS}/834/logo-fundo-azul.png`,
        branco: `${CDN_LOGOS}/834/logo-fundo-branco.png`,
        empilhada: true,
    },
};

// fundo escuro sem a versão dele usa a outra escura (as duas têm tinta clara); o papel, nunca
const ORDEM_DA_LOGO: Record<FundoDaLogo, FundoDaLogo[]> = { preto: ['preto', 'azul'], azul: ['azul', 'preto'], branco: ['branco'] };

export interface LogoAtelier {
    src: string;
    /** veio do mapa: já é a arte certa para o fundo (não precisa medir nem de placa) */
    propria: boolean;
    empilhada: boolean;
}

/** A logo para pintar sobre `fundo`: a arte própria do tenant para ele ou, sem ela, a do cadastro. */
export const logoAtelier = (site: any, fundo: FundoDaLogo): LogoAtelier => {
    const logos = LOGOS_POR_FUNDO[Number(site?.id)];
    const propria = logos ? ORDEM_DA_LOGO[fundo].map((f) => logos[f]).find(Boolean) : undefined;
    if (propria) return { src: propria, propria: true, empilhada: Boolean(logos?.empilhada) };
    return { src: site?.url_logo ? `${site.url_logo}?v=${site?.contador_marca || 0}` : '', propria: false, empilhada: false };
};

/** Nome que a imobiliária mostra (empresa) — sem HTML, aparado. */
export const nomeDaEmpresa =(site: any): string => String(site?.Dados?.empresa ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const SUFIXO_JURIDICO = /[\s,.\-–]*\b(ltda|eireli|epp|me|s\/a|s\.a|sa|mei|sociedade simples)\b\.?\s*$/i;
/** "Yes abc imóveis Eireli" → "Yes abc imóveis" (só para o nome gigante do rodapé; o copyright mantém o nome completo). */
export const nomeSemSufixoJuridico = (nome: string): string => {
    let n = String(nome ?? '').trim();
    for (let i = 0; i < 2; i++) n = n.replace(SUFIXO_JURIDICO, '').trim();
    return n || String(nome ?? '').trim();
};

export const stripHtml = (value: unknown): string =>
    String(value ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
